/**
 * StepAutocomplete Component
 *
 * Autocomplete input for selecting funnel steps, grouped by funnel name.
 * Fetches funnels and steps from the API. Supports search and custom values.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { funnelsApi } from '@/modules/funnels/services/funnelsApi';
import { Icon } from '@/shared/icons'
export interface StepAutocompleteProps {
  /** Current value (step ID) */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Additional className */
  className?: string;
}

interface StepItem {
  id: string;
  name: string;
  funnelId: string;
  funnelName: string;
}

interface FunnelGroup {
  funnelId: string;
  funnelName: string;
  steps: StepItem[];
}

/**
 * StepAutocomplete - Dropdown for selecting funnel steps grouped by funnel
 */
export const StepAutocomplete: React.FC<StepAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Selecione uma etapa...',
  disabled = false,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Fetch all funnels with their steps in a single cached query
  const { data: funnelsWithSteps } = useQuery({
    queryKey: ['funnels-with-steps-dropdown'],
    queryFn: async () => {
      const funnels = await funnelsApi.getFunnelsForDropdown();
      const results = await Promise.all(
        funnels.map(async (funnel) => {
          const steps = await funnelsApi.getStepsByFunnelId(funnel.id);
          const sorted = [...steps].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
          return { id: funnel.id, name: funnel.name, steps: sorted };
        })
      );
      return results;
    },
    staleTime: 10 * 60 * 1000,
  });

  // Build flat list of all steps with funnel info
  const allSteps = useMemo<StepItem[]>(() => {
    if (!funnelsWithSteps) return [];
    return funnelsWithSteps.flatMap(funnel =>
      funnel.steps.map((step: any) => ({
        id: step.id,
        name: step.name,
        funnelId: funnel.id,
        funnelName: funnel.name,
      }))
    );
  }, [funnelsWithSteps]);

  // Filter steps based on search term
  const filteredSteps = useMemo<StepItem[]>(() => {
    if (!searchTerm.trim()) return allSteps;
    const lower = searchTerm.toLowerCase();
    return allSteps.filter(
      s => s.name.toLowerCase().includes(lower) || s.funnelName.toLowerCase().includes(lower)
    );
  }, [allSteps, searchTerm]);

  // Group filtered steps by funnel (maintaining funnel order)
  const filteredGroups = useMemo<FunnelGroup[]>(() => {
    if (!funnelsWithSteps) return [];
    const stepsByFunnel = new Map<string, StepItem[]>();
    filteredSteps.forEach(step => {
      if (!stepsByFunnel.has(step.funnelId)) {
        stepsByFunnel.set(step.funnelId, []);
      }
      stepsByFunnel.get(step.funnelId)!.push(step);
    });

    // Maintain original funnel order
    return funnelsWithSteps
      .filter(f => stepsByFunnel.has(f.id))
      .map(f => ({
        funnelId: f.id,
        funnelName: f.name,
        steps: stepsByFunnel.get(f.id)!,
      }));
  }, [funnelsWithSteps, filteredSteps]);

  // Display label for selected value
  const displayLabel = useMemo(() => {
    if (!value) return '';
    const step = allSteps.find(s => s.id === value);
    return step?.name || value;
  }, [value, allSteps]);

  const isKnownStep = useMemo(() => {
    if (!value) return false;
    return allSteps.some(s => s.id === value);
  }, [value, allSteps]);

  // Custom value option
  const showCustomOption = useMemo(() => {
    if (!searchTerm.trim()) return false;
    return !allSteps.some(s => s.id === searchTerm.trim());
  }, [searchTerm, allSteps]);

  const totalItems = filteredSteps.length + (showCustomOption ? 1 : 0);

  // Update dropdown position
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 320),
      });
    }
  }, [isOpen]);

  // Reset selected index when filtered steps change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredSteps.length]);

  // Scroll selected item into view
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const el = dropdownRef.current.querySelector('[data-selected="true"]');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (step: StepItem) => {
    onChange(step.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, totalItems - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (showCustomOption && selectedIndex === filteredSteps.length) {
          onChange(searchTerm.trim());
          setIsOpen(false);
          setSearchTerm('');
        } else if (filteredSteps[selectedIndex]) {
          handleSelect(filteredSteps[selectedIndex]);
        } else if (showCustomOption) {
          onChange(searchTerm.trim());
          setIsOpen(false);
          setSearchTerm('');
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
      case 'Tab':
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleContainerClick = () => {
    if (disabled) return;
    setIsOpen(true);
    inputRef.current?.focus();
  };

  // Highlight matching text
  const highlightMatch = (text: string) => {
    if (!searchTerm) return text;
    const lowerText = text.toLowerCase();
    const lowerSearch = searchTerm.toLowerCase();
    const index = lowerText.indexOf(lowerSearch);
    if (index === -1) return text;
    return (
      <>
        {text.slice(0, index)}
        <span className="bg-yellow-200 text-yellow-900">
          {text.slice(index, index + searchTerm.length)}
        </span>
        {text.slice(index + searchTerm.length)}
      </>
    );
  };

  // Render dropdown
  const renderDropdown = () => {
    if (!isOpen) return null;

    let currentIndex = 0;

    return createPortal(
      <div
        ref={dropdownRef}
        className="fixed z-[9999] bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto"
        style={{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
        }}
      >
        {/* Search input */}
        <div className="sticky top-0 bg-white border-b border-gray-100 p-2">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Buscar etapa ou funil..."
            className="w-full h-[38px] px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            autoFocus
          />
        </div>

        {/* Steps grouped by funnel */}
        {filteredSteps.length > 0 ? (
          <div className="py-1">
            {filteredGroups.map((group) => (
              <div key={group.funnelId}>
                {/* Funnel header */}
                <div className="px-3 py-1.5 bg-gray-50 border-y border-gray-100">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    🔄 {highlightMatch(group.funnelName)}
                  </span>
                </div>

                {/* Steps in funnel */}
                {group.steps.map((step) => {
                  const isSelected = currentIndex === selectedIndex;
                  const itemIndex = currentIndex;
                  currentIndex++;

                  return (
                    <button
                      key={step.id}
                      type="button"
                      data-selected={isSelected}
                      className={cn(
                        'w-full px-3 py-2 text-left hover:bg-gray-100 transition-colors',
                        isSelected && 'bg-violet-50'
                      )}
                      onClick={() => handleSelect(step)}
                      onMouseEnter={() => setSelectedIndex(itemIndex)}
                    >
                      <div className="text-sm text-gray-900 truncate">
                        {highlightMatch(step.name)}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}

            {/* Custom value option when results exist */}
            {showCustomOption && (
              <div className="border-t border-gray-100">
                <button
                  type="button"
                  data-selected={selectedIndex === filteredSteps.length}
                  className={cn(
                    'w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-gray-100 transition-colors',
                    selectedIndex === filteredSteps.length && 'bg-violet-50'
                  )}
                  onClick={() => {
                    onChange(searchTerm.trim());
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  onMouseEnter={() => setSelectedIndex(filteredSteps.length)}
                >
                  <span className="text-sm text-violet-600 flex-shrink-0">Usar:</span>
                  <span className="text-sm text-gray-900 font-mono truncate">
                    {searchTerm.trim()}
                  </span>
                </button>
              </div>
            )}
          </div>
        ) : showCustomOption ? (
          <div className="py-1">
            <button
              type="button"
              data-selected={selectedIndex === 0}
              className={cn(
                'w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-gray-100 transition-colors',
                selectedIndex === 0 && 'bg-violet-50'
              )}
              onClick={() => {
                onChange(searchTerm.trim());
                setIsOpen(false);
                setSearchTerm('');
              }}
              onMouseEnter={() => setSelectedIndex(0)}
            >
              <span className="text-sm text-violet-600 flex-shrink-0">Usar:</span>
              <span className="text-sm text-gray-900 font-mono truncate">
                {searchTerm.trim()}
              </span>
            </button>
          </div>
        ) : (
          <div className="px-3 py-6 text-center text-sm text-gray-500">
            {!funnelsWithSteps ? 'Carregando funis...' : 'Nenhuma etapa encontrada'}
          </div>
        )}

        {/* Help footer */}
        <div className="sticky bottom-0 px-3 py-1.5 bg-gray-50 border-t text-[10px] text-gray-400 flex justify-between">
          <span>↑↓ navegar</span>
          <span>Enter selecionar</span>
          <span>Esc fechar</span>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Main input container */}
      <div
        onClick={handleContainerClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        className={cn(
          'w-full h-[38px] px-3 py-2 border rounded-lg cursor-pointer flex items-center justify-between gap-2',
          'focus:outline-none focus:ring-2 focus:ring-violet-500',
          disabled && 'cursor-not-allowed opacity-60',
          isOpen && 'ring-2 ring-violet-500'
        )}
        style={{
          backgroundColor: disabled ? 'var(--theme-button-ghost-hover)' : 'var(--theme-input-background)',
          borderColor: isOpen ? '#8b5cf6' : 'var(--theme-input-border)',
          color: 'var(--theme-input-text)',
        }}
      >
        <span
          className={cn(
            'text-sm truncate',
            value ? (isKnownStep ? '' : 'font-mono') : ''
          )}
          style={{
            color: value ? 'var(--theme-card-text)' : 'var(--theme-muted-text)',
          }}
        >
          {displayLabel || placeholder}
        </span>
        <Icon name="chevron-down" className={cn(
            'w-4 h-4 flex-shrink-0 transition-transform',
            isOpen && 'rotate-180'
          )}
          style={{ color: 'var(--theme-muted-text)' }} />
      </div>

      {/* Dropdown */}
      {renderDropdown()}
    </div>
  );
};

StepAutocomplete.displayName = 'StepAutocomplete';

export default StepAutocomplete;
